import { UserTypes } from '$types/UserTypes';
import getUserType from '$utils/getUserType';
import { AutoCompleteState } from 'primereact/autocomplete';
import { ButtonProps } from 'primereact/button';
import { ChipsState } from 'primereact/chips';
import {
  DropdownContext,
  DropdownProps,
  DropdownState,
} from 'primereact/dropdown';
import { MultiSelectProps, MultiSelectState } from 'primereact/multiselect';
import { classNames } from 'primereact/utils';

export const SimeraDesignSystem = {
  button: {
    root: ({ props }: { props: ButtonProps }) => ({
      className: classNames(
        'text-sm font-bold shadow-none justify-center overflow-visible rounded-full h-fit',
        {
          'text-employer-primary  bg-spk-softBlue border-none hover:bg-spk-softBlue-110':
            props.severity === 'secondary' &&
            !props.text &&
            !props.outlined &&
            !props.plain,
          'text-spk-softBlue bg-employer-primary  border-none hover:bg-employer-primary110':
            props.severity === 'success' &&
            !props.text &&
            !props.outlined &&
            !props.plain,
          'text-neutral-0 bg-spk-hotPink border-none hover:bg-hotPink110':
            props.severity === 'contrast' &&
            !props.text &&
            !props.outlined &&
            !props.plain,
        },
        {
          'text-employer-primary border border-solid border-spk-softBlue ':
            props.outlined && props.severity === 'success' && !props.plain,
          'text-spk-mediumContrast border border-solid border-employer-secondary ':
            props.outlined && props.severity === 'secondary' && !props.plain,
        },
        {
          'text-employer-primary ':
            props.text &&
            props.severity === 'success' &&
            !props.outlined &&
            !props.plain,
          'text-spk-mediumContrast':
            props.text &&
            props.severity === 'secondary' &&
            !props.outlined &&
            !props.plain,
        },
        {
          'text-employer-primary  bg-spk-neutral-0':
            !props.text &&
            props.link &&
            props.severity === 'success' &&
            !props.outlined &&
            !props.plain,
        },
        {
          'px-4 py-2 text-xs': props.size === 'small',
          'px-5 py-3': props.size !== 'small',
        }
      ),
    }),
  },
  IconField: {
    root: {
      className: 'bg-transparent',
    },
  },
  inputicon: {
    root: {
      className: 'text-spk-mediumContrast',
    },
  },
  inputtext: {
    root: {
      className: classNames(
        'font-mulish py-3 rounded-md bg-spk-neutral-0 text-spk-highContrast text-sm rounded-md focus:border-spk-hotPink hover:border-spk-hotPink enabled:focus:shadow-[0_0_0_3px_rgba(243,122,216,0.12)]',
        'font-semibold placeholder:font-normal border-employer-secondary placeholder:text-spk-mediumContrast placeholder:font-mulish'
      ),
    },
  },
  inputtextarea: {
    root: () => ({
      className: classNames(
        'font-mulish bg-white text-spk-highContrast text-sm focus:border-spk-hotPink rounded-md hover:border-spk-hotPink enabled:focus:shadow-[0_0_0_3px_rgba(243,122,216,0.12)]',
        'font-semibold placeholder:font-normal border-employer-secondary placeholder:text-spk-mediumContrast placeholder:font-mulish'
      ),
    }),
  },
  divider: {
    root: {
      className: 'before:content-[""] before:border-employer-secondary',
    },
    content: {
      className:
        'bg-employer-background text-sm font-semibold text-spk-mediumContrast',
    },
  },
  floatlabel: {
    root: {
      className: classNames(
        '[&>input]:font-normal',
        '[&>input:focus]:border-internal-main [&>input:focus:enabled]:shadow-none',
        '[&>input:hover]:border-internal-main',
        '[&>input.p-invalid]:border-red-500',
        '[&>input.p-invalid:hover]:border-red-600',
        '[&>input.p-invalid:focus]:border-red-700',
        '[&>input:focus:enabled]:shadow-none'
      ),
    },
  },
  calendar: {
    root: () => {
      const currentUserRole = getUserType();
      return {
        className: `
        [&>input]:font-normal
        [&>input:focus]:${
          currentUserRole === UserTypes.INTERNAL
            ? 'border-internal-main'
            : 'border-spk-hotPink'
        }
        [&>input:hover]:${
          currentUserRole === UserTypes.INTERNAL
            ? 'border-internal-main'
            : 'border-spk-hotPink'
        }
        [&>input:focus:enabled]:shadow-none
        [&>input:hover:enabled]:shadow-none
        [&>input:hover:enabled]:shadow-none
        `,
      };
    },
  },
  tabpanel: {
    headerAction: ({ parent, context }: any) => ({
      className: classNames({
        'bg-white border-internal-main text-internal-main':
          parent != null ? parent.state.activeIndex === context.index : false, // Condition-based active styles.
      }),
    }),
  },
  inputnumber: {
    input: {
      root: () => ({
        className: classNames('border border-solid !border-internal-main'),
      }),
    },
  },
  selectButton: {
    root: { className: 'flex flex-row' },
    button: ({ context }: any) => {
      return {
        className: `${
          context.selected &&
          'border-employer-primary bg-spk-softBlue text-employer-primary border-r border-solid'
        } w-full shadow-none`,
      };
    },
  },
  dropdown: {
    root: ({ state }: { state: DropdownState }) => ({
      className: classNames(
        'bg-white text-sm text-spk-highContrast hover:border-spk-hotPink border border-employer-secondary rounded-md',
        {
          'border-spk-hotPink shadow-[0_0_0_3px_rgba(243,122,216,0.12)]':
            state.focused,
        }
      ),
    }),
    trigger: () => ({
      className: 'text-employer-primary',
    }),
    panel: {
      className:
        'text-sm bg-white border border border-solid border-employer-secondary shadow-[0_4px_32px_1px_rgba(106,103,90,0.10)]',
    },
    emptyMessage: {
      className: 'text-spk-highContrast',
    },
    header: {
      className: 'bg-spk-neutral-0 border-employer-secondary',
    },
    input: ({ props }: { props: DropdownProps }) => {
      return {
        className: classNames(
          'py-3 text-sm font-semibold placeholder:text-spk-mediumContrast',
          {
            'text-spk-mediumContrast !font-normal': !props.value,
            'text-spk-highContrast': props.value,
          }
        ),
      };
    },
    filterInput: () => ({
      className: classNames(
        'bg-white text-spk-highContrast text-sm',
        'font-semibold placeholder:font-normal border-employer-secondary hover:border-spk-hotPink',
        'placeholder:text-spk-mediumContrast',
        'enabled:focus:shadow-[0_0_0_3px_rgba(243,122,216,0.12)] enabled:focus:border-spk-hotPink'
      ),
    }),
    item: ({ context }: { context: DropdownContext }) => ({
      className: classNames(
        'text-spk-highContrast hover:bg-spk-neutral-20 px-4 font-semibold focus:outline-none',
        {
          'bg-employer-secondary/60': context.selected,
        }
      ),
    }),
    clearIcon: {
      className: 'text-spk-mediumContrast',
    },
  },
  checkbox: {
    root: {
      className: `
        [&:not(.p-disabled):has(.p-checkbox-input:hover)_.p-checkbox-box]:border-employer-primary
      `,
    },
    box: ({ context }: any) => {
      return {
        className: `${
          context.checked && 'bg-employer-primary border-employer-primary'
        } shadow-none outline-none`,
      };
    },
  },
  autocomplete: {
    container: ({ state }: { state: AutoCompleteState }) => ({
      className: classNames(
        'bg-white hover:border-spk-hotPink border-employer-secondary rounded-md py-3 px-2',
        {
          'border-spk-hotPink shadow-[0_0_0_3px_rgba(243,122,216,0.12)]':
            state.focused,
        }
      ),
    }),
    token: () => ({
      className: 'text-sm bg-spk-neutral-20 text-spk-highContrast rounded-full',
    }),
    removeTokenIcon: {
      className: 'text-spk-mediumContrast',
    },
    inputToken: {
      className: '[&>input]:text-sm [&>input]:text-spk-highContrast',
    },
    panel: () => ({
      className:
        'border-employer-secondary bg-white shadow-[0_4px_32px_1px_rgba(106,103,90,0.10)] autocomplete',
    }),
    item: () => ({
      className:
        'px-4 py-3 text-spk-highContrast text-sm hover:bg-spk-neutral-20',
    }),
    emptyMessage: () => ({
      className:
        'px-4 py-3 text-spk-highContrast text-sm hover:bg-spk-neutral-20',
    }),
  },
  multiselect: {
    root: ({ state }: { state: MultiSelectState }) => ({
      className: classNames(
        'text-sm text-spk-highContrast bg-white border-employer-secondary hover:border-spk-hotPink rounded-md',
        {
          'border-spk-hotPink shadow-[0_0_0_3px_rgba(243,122,216,0.12)]':
            state.focused,
        }
      ),
    }),
    label: ({ props }: { props: MultiSelectProps }) => ({
      className: classNames('py-3', {
        'text-spk-mediumContrast': !props.value?.length,
        'text-spk-highContrast font-semibold': props.value?.length,
      }),
    }),
    token: {
      className: 'bg-spk-neutral-20 text-spk-highContrast',
    },
    trigger: {
      className: 'text-employer-primary',
    },
    panel: {
      className:
        'text-sm bg-white border-employer-secondary shadow-[0_4px_32px_1px_rgba(106,103,90,0.10)]',
    },
    item: ({
      context,
    }: {
      context: { focused: boolean; selected: boolean };
    }) => ({
      className: classNames(
        'text-sm text-spk-highContrast hover:bg-spk-neutral-20 px-4',
        {
          'bg-spk-neutral-0': !context.focused && context.selected,
        }
      ),
    }),
    header: {
      className: 'bg-spk-neutral-0',
    },
    checkbox: {
      icon: {
        className: 'text-spk-neutral-0',
      },
    },
  },
  chips: {
    container: ({ state }: { state: ChipsState }) => ({
      className: classNames(
        'bg-white border-employer-secondary hover:border-spk-hotPink',
        {
          'border-spk-hotPink shadow-[0_0_0_3px_rgba(243,122,216,0.12)]':
            state.focused,
        }
      ),
    }),
    input: () => ({
      className: 'text-spk-mediumContrast placeholder:text-spk-mediumContrast',
    }),
    token: () => ({
      className: 'text-spk-highContrast bg-spk-neutral-20',
    }),
    removeTokenIcon: {
      className: 'text-spk-mediumContrast',
    },
  },
};
